import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { organization, user } from '../auth'
import { MyBookCollection } from './my-book-collection'

export const SentBookCollection = pgTable(
  'sent_book_collection',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    myBookCollectionId: uuid()
      .notNull()
      .references(() => MyBookCollection.id, { onDelete: 'cascade' }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (table) => [
    index('idx_sent_book_collection_user').on(table.userId),
    index('idx_sent_book_collection_organization').on(table.organizationId),
    index('idx_sent_book_collection_my_book_collection').on(table.myBookCollectionId),
  ],
)

export const SentBookCollectionToUser = pgTable(
  'sent_book_collection_to_user',
  {
    id: uuid().primaryKey().defaultRandom(),
    sentBookCollectionId: uuid()
      .notNull()
      .references(() => SentBookCollection.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    readAt: timestamp('read_at', { mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    index('idx_sent_book_collection_to_user_sent').on(table.sentBookCollectionId),
    index('idx_sent_book_collection_to_user_user').on(table.userId),
  ],
)

export const sentBookCollectionRelations = relations(SentBookCollection, ({ one, many }) => ({
  user: one(user, {
    fields: [SentBookCollection.userId],
    references: [user.id],
  }),

  organization: one(organization, {
    fields: [SentBookCollection.organizationId],
    references: [organization.id],
  }),

  myBookCollection: one(MyBookCollection, {
    fields: [SentBookCollection.myBookCollectionId],
    references: [MyBookCollection.id],
  }),

  sentBookCollectionToUsers: many(SentBookCollectionToUser),
}))

export const sentBookCollectionToUserRelations = relations(SentBookCollectionToUser, ({ one }) => ({
  sentBookCollection: one(SentBookCollection, {
    fields: [SentBookCollectionToUser.sentBookCollectionId],
    references: [SentBookCollection.id],
  }),

  user: one(user, {
    fields: [SentBookCollectionToUser.userId],
    references: [user.id],
  }),
}))

export const InsertSentBookCollectionSchema = createInsertSchema(SentBookCollection).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateSentBookCollectionSchema = createUpdateSchema(SentBookCollection).partial().omit({
  id: true,
  userId: true,
  organizationId: true,
  myBookCollectionId: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectSentBookCollectionSchema = createSelectSchema(SentBookCollection)

export const InsertSentBookCollectionToUserSchema = createInsertSchema(SentBookCollectionToUser).omit({
  id: true,
  createdAt: true,
})

export const UpdateSentBookCollectionToUserSchema = createUpdateSchema(SentBookCollectionToUser).partial().omit({
  id: true,
  sentBookCollectionId: true,
  userId: true,
  createdAt: true,
})

export const SelectSentBookCollectionToUserSchema = createSelectSchema(SentBookCollectionToUser)

export type InsertSentBookCollection = typeof SentBookCollection.$inferInsert
export type UpdateSentBookCollection = z.infer<typeof UpdateSentBookCollectionSchema>
export type SelectSentBookCollection = typeof SentBookCollection.$inferSelect
export type InsertSentBookCollectionToUser = typeof SentBookCollectionToUser.$inferInsert
export type UpdateSentBookCollectionToUser = z.infer<typeof UpdateSentBookCollectionToUserSchema>
export type SelectSentBookCollectionToUser = typeof SentBookCollectionToUser.$inferSelect
